import { compareTreeOrder } from "./entityTree";
import { queryOptions } from "@tanstack/react-query";
import { unwrap, type Schemas } from "@tern/api-client";
import { api } from "./api";

export type DocMeta = Schemas["DocMeta"];
export type Doc = Schemas["Doc"];
export type DocLink = Schemas["DocLink"];
export type DocVersion = Schemas["DocVersion"];

export const docsQuery = (slug: string, key: string) =>
  queryOptions({
    queryKey: ["docs", slug, key],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/projects/{key}/docs", { params: { path: { slug, key } } })),
    staleTime: 15_000,
  });

export const docQuery = (id: string) =>
  queryOptions({
    queryKey: ["doc", id],
    queryFn: async () => unwrap(await api.GET("/api/docs/{id}", { params: { path: { id } } })),
    staleTime: Infinity,
  });

export const docLinksQuery = (id: string) =>
  queryOptions({ queryKey: ["doc", id, "links"], queryFn: async () => unwrap(await api.GET("/api/docs/{id}/links", { params: { path: { id } } })) });

export const docVersionsQuery = (id: string) =>
  queryOptions({ queryKey: ["doc", id, "versions"], queryFn: async () => unwrap(await api.GET("/api/docs/{id}/versions", { params: { path: { id } } })) });

export const issueDocsQuery = (issueId: string) =>
  queryOptions({ queryKey: ["issue", issueId, "docs"], queryFn: async () => unwrap(await api.GET("/api/issues/{id}/docs", { params: { path: { id: issueId } } })) });

export const taskDocsQuery = (taskId: string) =>
  queryOptions({ queryKey: ["task", taskId, "docs"], queryFn: async () => unwrap(await api.GET("/api/tasks/{id}/docs", { params: { path: { id: taskId } } })) });

export const createDoc = async (slug: string, key: string, body: { title: string; parent_id?: string | null; content?: string | null }) =>
  unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/docs", { params: { path: { slug, key } }, body }));

export const updateDoc = async (id: string, body: Schemas["UpdateDoc"]) => unwrap(await api.PATCH("/api/docs/{id}", { params: { path: { id } }, body }));
export const deleteDoc = async (id: string) => unwrap(await api.DELETE("/api/docs/{id}", { params: { path: { id } } }));
export const restoreVersion = async (id: string) => unwrap(await api.POST("/api/doc-versions/{id}/restore", { params: { path: { id } } }));
export const versionContent = async (id: string) => unwrap(await api.GET("/api/doc-versions/{id}", { params: { path: { id } } }));

/** Tree of docs (parent → children), sorted by sort_order. */
export type DocNode = { doc: DocMeta; depth: number; children: DocNode[] };
export function docTree(list: DocMeta[]): DocNode[] {
  const live = list.filter((d) => !d.deleted_at && !d.archived_at);
  const ids = new Set(live.map((d) => d.id));
  const build = (d: DocMeta, depth: number): DocNode => ({
    doc: d,
    depth,
    children: live.filter((c) => c.parent_id === d.id).sort(compareTreeOrder).map((c) => build(c, depth + 1)),
  });
  return live
    .filter((d) => !d.parent_id || !ids.has(d.parent_id))
    .sort(compareTreeOrder)
    .map((d) => build(d, 0));
}

/** Download text as a file from inside the app. */
export function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
